import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Modal } from '@kep-ui-kit/ui-components';
import { Alert24Icon } from '@kep-ui-kit/ui-icons';
import { FormikProps } from 'formik';

import { CheckoutForm } from '@selfserve/react-feature-purchase-plan';
import {
  StyledModalContainer,
  StyledModalTitle,
  StyledButton,
  StyledContainer,
} from '@selfserve/react-ui';
import { FormValues } from '@selfserve/react-hooks';
import { buttonIds, cardFormTypes } from '@selfserve/react-utils';

import { StyledErrorContainer } from '../feature/billing-details';

const StyledBillingModal = styled(Modal)(({ theme }: { theme?: any }) => ({
  '.MuiPaper-root': {
    maxWidth: '584px',
    maxHeight: '600px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      margin: '0',
      maxHeight: '100%',
    },
  },
}));

const StyledBillingModalTitle = styled(StyledModalTitle)(
  ({ theme }: { theme?: any }) => ({
    marginBottom: theme.spacing(5),
    textTransform: 'capitalize',
    [theme.breakpoints.down('sm')]: {
      marginTop: '20px',
    },
  })
);

const StyledButtonsContainer = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  marginTop: 'auto',
  padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
  borderTop: `1px solid ${theme.palette.grey['400']}`,
  justifyContent: 'flex-end',
  '> :first-of-type': {
    marginRight: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    justifyContent: 'center',
  },
}));

const StyledForm = styled('form')`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

interface Props {
  open: boolean;
  handleClose: () => void;
  formik: FormikProps<FormValues>;
  handleDropdownChange: (name: string) => (value: string) => void;
  handleCardInputsChange: (fieldData: any) => void;
  handleCardInputsBlur: (fieldData: any) => void;
  forwardRef: React.MutableRefObject<HTMLElement | undefined>;
  handleSubmit: () => void;
  chargebeeInited: boolean;
  activeFormSection: string;
  isLoading: boolean;
  apiError: string;
}

export const StyledFormContainer = styled(StyledContainer, {
  shouldForwardProp: (prop) => {
    return prop !== 'withoutMargin';
  },
})(({ theme, withoutMargin }: { theme?: any; withoutMargin?: boolean }) => ({
  flex: 1,
  height: 'fit-content',
  [theme.breakpoints.up('md')]: {
    marginRight: theme.spacing(4),
    marginBottom: 0,
  },
  ...(!withoutMargin && {
    '> div': {
      marginBottom: '32px',
    },
  }),
}));

const inValidForm = (
  activeFormSection: string,
  isValid: boolean,
  values: FormValues
) => {
  switch (activeFormSection) {
    case cardFormTypes.ADD_PAYMENT_METHOD:
    case cardFormTypes.CHANGE_PAYMENT_METHOD:
      return !isValid || !values?.number;
    case cardFormTypes.EDIT_BILLING_INFO:
      return !isValid || !values?.address;
    default:
      return true;
  }
};

export const BillingDetailsModal = ({
  open,
  formik,
  handleDropdownChange,
  handleCardInputsChange,
  handleCardInputsBlur,
  forwardRef,
  handleClose,
  handleSubmit,
  chargebeeInited,
  activeFormSection,
  isLoading,
  apiError,
}: Props) => {
  const { t } = useTranslation();
  const billingFormTitle = t(`billing-details.${activeFormSection}`);
  const { isValid, values } = formik;
  return (
    <StyledBillingModal
      backdropColor="rgba(0,110,250,.8)"
      onClose={handleClose}
      fullWidth
      open={open}
    >
      <StyledForm onSubmit={handleSubmit}>
        <StyledModalContainer>
          <StyledBillingModalTitle className="dialog-title">
            {billingFormTitle}
          </StyledBillingModalTitle>
          <StyledFormContainer withoutMargin>
            {chargebeeInited && (
              <CheckoutForm
                formik={formik}
                handleDropdownChange={handleDropdownChange}
                handleCardInputsChange={handleCardInputsChange}
                handleCardInputsBlur={handleCardInputsBlur}
                ref={forwardRef}
                reverse
                activeFormSection={activeFormSection}
              />
            )}
          </StyledFormContainer>
          {apiError && (
            <StyledErrorContainer withoutPadding>
              <Alert24Icon className="err-icon" />
              <span>{apiError}</span>
            </StyledErrorContainer>
          )}
        </StyledModalContainer>
        <StyledButtonsContainer>
          <StyledButton
            withoutMargin
            isAddOn
            onClick={handleClose}
            isBorderless
            id={
              activeFormSection === cardFormTypes.EDIT_BILLING_INFO
                ? buttonIds.changeBillingInformationCancel
                : buttonIds.changePaymentMethodCancel
            }
            tabIndex={1}
          >
            {t('common.cancel')}
          </StyledButton>
          <StyledButton
            withoutMargin
            type="submit"
            loading={isLoading}
            variant="borderless"
            disabled={inValidForm(activeFormSection, isValid, values)}
            isBorderless
            id={
              activeFormSection === cardFormTypes.EDIT_BILLING_INFO
                ? buttonIds.changeBillingInformationApply
                : buttonIds.changePaymentMethodApply
            }
          >
            {t('common.apply')}
          </StyledButton>
        </StyledButtonsContainer>
      </StyledForm>
    </StyledBillingModal>
  );
};
